import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { ActivityPrefs } from '@volley/crucible-ipc-types/ipc-channels';

/**
 * Thin React Query wrapper around the C1 `activity-prefs:load` /
 * `activity-prefs:save` sidecar (Crucible #602, design plan §7).
 *
 * Used by `<TemplateTooOldBanner>` to gate visibility on the
 * persisted `templateWarningDismissed` flag. The hook also
 * exposes an imperative `setTemplateWarningDismissed` writer that
 * mutates the cache optimistically so the banner unmounts on the
 * same render the user clicks dismiss — no waiting for the IPC
 * round-trip.
 *
 * Codex round-1 HIGH on the C2 design (see `docs/c2-transparency-
 * banner-design.md` §7) flagged that visibility MUST be derived
 * from the persisted prefs + the snapshot, NOT from the
 * once-per-(gameId, sessionId) `activity:template-warning` push.
 * That push is retained for analytics; this hook is the gating
 * signal.
 *
 * `data` semantics:
 *   - `undefined` while the load is in flight (first paint after
 *     mount) — callers MUST treat this as "don't know yet, do not
 *     render" to avoid a flash of the banner before the dismissed
 *     flag has been read.
 *   - `null` when the sidecar file does not exist (no prefs ever
 *     written for this game).
 *   - `ActivityPrefs` when the sidecar has been written at least
 *     once for this game.
 */
const ACTIVITY_PREFS_OPTIONS = {
    // Prefs only change via this hook's own mutator or by the
    // registry clearing the dismissal when the template version
    // catches up (design §7 "Subtle edge case"). For the latter we
    // refetch on mount so a stale Workspace remount sees the
    // cleared flag, but we don't need aggressive polling — there
    // is no user-visible jitter from a few seconds of staleness.
    staleTime: 30 * 1000,
    refetchOnMount: 'always',
    refetchOnWindowFocus: false,
    retry: false,
} as const;

const queryKey = (gameId: string | undefined): readonly unknown[] =>
    ['activity-prefs:load', gameId];

export function useActivityPrefs(
    gameId: string | undefined,
    options: { enabled?: boolean } = {},
): {
    data: ActivityPrefs | null | undefined;
    isLoading: boolean;
    setTemplateWarningDismissed: (dismissed: boolean) => Promise<void>;
} {
    const queryClient = useQueryClient();
    const gameIdReady = typeof gameId === 'string' && gameId.length > 0;
    const enabled = gameIdReady && (options.enabled ?? true);
    const query = useQuery<ActivityPrefs | null>({
        queryKey: queryKey(gameId),
        queryFn: async () => {
            if (!gameId) throw new Error('gameId is required');
            return await window.crucible.invoke('activity-prefs:load', { gameId });
        },
        enabled,
        ...ACTIVITY_PREFS_OPTIONS,
    });

    const setTemplateWarningDismissed = async (dismissed: boolean): Promise<void> => {
        if (!gameId) return;
        const next: ActivityPrefs = {
            templateWarningDismissed: dismissed,
            schemaVersion: 1,
        };
        // Optimistic cache write so the banner unmounts on the
        // same render the dismiss button fires. The IPC save
        // happens in parallel; if it fails the next mount-driven
        // refetch will read ground truth from disk.
        queryClient.setQueryData<ActivityPrefs | null>(queryKey(gameId), next);
        await window.crucible.invoke('activity-prefs:save', { gameId, prefs: next });
    };

    return {
        data: query.data,
        isLoading: query.isLoading,
        setTemplateWarningDismissed,
    };
}
